import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { money, fmtDateShort, isoDate, today } from "../format.js";
import {
  Async, Badge, BarList, Button, Card, Check, Chips, Empty, ErrorPanel, Field, Fields, Grid, Input,
  KPI, Modal, Select, Sep, Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const CATEGORIES = ["Software", "Travel", "Hosting", "Assets", "Office", "Meals", "Subcontractor", "Other"];

export default function Expenses() {
  const toast = useToast();
  const { settings, me } = useApp();
  const canSubmit = !!me.permissions["expenses.submit"];
  const state = useAsync(() => api.get("expenses"), []);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // an expense, or {} for a new one
  const sym = settings.currency_symbol;
  const monthStart = `${today().slice(0, 7)}-01`;

  return (
    <>
      <TopBar title="Expenses">
        {canSubmit && (
          <Button kind="pri" onClick={() => setEditing({})}>
            ＋ New expense
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => {
            const list = d.list || [];
            const thisMonth = list.filter((x) => isoDate(x.spent_on) >= monthStart);
            const monthTotal = thisMonth.reduce((s, x) => s + Number(x.amount || 0), 0);
            const rows =
              filter === "rebillable"
                ? list.filter((x) => x.billable && !x.invoice_id)
                : filter === "missing"
                ? list.filter((x) => !x.receipt_blob_key)
                : list;

            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="This month"
                    value={money(monthTotal, sym)}
                    sub={`${thisMonth.length} expense${thisMonth.length === 1 ? "" : "s"}`}
                  />
                  <KPI label="Rebillable" value={money(d.totals?.rebillable, sym)} sub="not yet invoiced" />
                  <KPI label="Recoverable tax" value={money(d.totals?.tax, sym)} sub="input tax credits" />
                  <KPI
                    label="Missing receipts"
                    value={Number(d.totals?.missing_receipts || 0)}
                    tone={Number(d.totals?.missing_receipts || 0) ? "warn" : undefined}
                    sub={Number(d.totals?.missing_receipts || 0) ? "needs attention" : "all receipts attached"}
                  />
                </Grid>

                <Grid cols="1.7fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card
                    bare
                    title="All expenses"
                    right={
                      <Chips
                        value={filter}
                        onChange={setFilter}
                        options={[
                          { key: "all", label: "All", count: list.length },
                          { key: "rebillable", label: "Rebillable" },
                          { key: "missing", label: "Missing receipts" },
                        ]}
                      />
                    }
                  >
                    <Table
                      rows={rows}
                      onRowClick={canSubmit ? (x) => setEditing(x) : undefined}
                      empty={<Empty icon="◰">No expenses recorded</Empty>}
                      columns={[
                        {
                          key: "spent_on",
                          label: "Date",
                          render: (x) => <span className="sm dim">{fmtDateShort(x.spent_on)}</span>,
                        },
                        {
                          key: "vendor",
                          label: "Vendor",
                          render: (x) => (
                            <>
                              <b className="sm">{x.vendor || "—"}</b>
                              <div className="tiny muted">
                                {(x.created_by_name || "").split(" ")[0]}
                                {x.receipt_blob_key ? null : (
                                  <>
                                    {x.created_by_name ? " · " : ""}
                                    <span style={{ color: "var(--warn)" }}>no receipt</span>
                                  </>
                                )}
                              </div>
                            </>
                          ),
                        },
                        { key: "category", label: "Category", render: (x) => <span className="sm dim">{x.category}</span> },
                        {
                          key: "where",
                          label: "Client / project",
                          render: (x) => (
                            <>
                              {x.client_name ? <span className="sm">{x.client_name}</span> : <span className="muted">—</span>}
                              {x.project_name && <div className="tiny muted">{x.project_name}</div>}
                            </>
                          ),
                        },
                        {
                          key: "amount",
                          label: "Amount",
                          align: "right",
                          render: (x) => <span className="num">{money(x.amount, sym)}</span>,
                        },
                        {
                          key: "tax_amount",
                          label: "Tax",
                          align: "right",
                          render: (x) => <span className="num dim">{money(x.tax_amount, sym)}</span>,
                        },
                        {
                          key: "rebill",
                          label: "Rebill",
                          render: (x) =>
                            x.invoice_id ? (
                              <Badge status="billed" label="Rebilled" />
                            ) : x.billable ? (
                              <Badge status="unbilled" label="Rebill" />
                            ) : (
                              <Badge status="draft" label="Absorbed" />
                            ),
                        },
                        {
                          key: "receipt",
                          label: "",
                          align: "right",
                          render: (x) =>
                            x.receipt_blob_key ? (
                              <span
                                className="linky sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  api
                                    .openBlob(api.filePath(x.receipt_blob_key))
                                    .catch((err) => toast.push(err.message, "err"));
                                }}
                              >
                                Receipt
                              </span>
                            ) : (
                              <span className="muted">›</span>
                            ),
                        },
                      ]}
                    />
                  </Card>

                  <Card title="By category">
                    {d.byCategory?.length ? (
                      <BarList
                        rows={d.byCategory.map((c) => ({ label: c.category, value: c.amount }))}
                        format={(v) => money(v, sym)}
                      />
                    ) : (
                      <Empty icon="◰">Nothing categorised yet</Empty>
                    )}
                    <Sep />
                    <div className="tiny muted">
                      Attach a receipt to every expense — the tax column only counts toward input tax credits when
                      there's proof on file.
                    </div>
                  </Card>
                </Grid>
              </>
            );
          }}
        </Async>
      </Wrap>

      {editing && (
        <ExpenseModal expense={editing} onClose={() => setEditing(null)} onSaved={state.reload} />
      )}
    </>
  );
}

/* -------------------------------------------------------------- expense modal */

function ExpenseModal({ expense, onClose, onSaved }) {
  const { settings, clients, projects } = useApp();
  const toast = useToast();
  const isNew = !expense.id;
  const sym = settings.currency_symbol;

  const [form, setForm] = useState({
    spent_on: isoDate(expense.spent_on) || today(),
    vendor: expense.vendor || "",
    category: expense.category || "Software",
    amount: expense.amount ?? "",
    tax_amount: expense.tax_amount ?? "",
    client_id: expense.client_id || "",
    project_id: expense.project_id || "",
    billable: !!expense.billable,
    rebill_amount: expense.rebill_amount ?? "",
  });
  const [receiptKey, setReceiptKey] = useState(expense.receipt_blob_key || null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const categories = CATEGORIES.includes(form.category) ? CATEGORIES : [form.category, ...CATEGORIES];

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () => {
    const body = {
      spent_on: form.spent_on,
      vendor: form.vendor,
      category: form.category,
      amount: Number(form.amount) || 0,
      tax_amount: Number(form.tax_amount) || 0,
      client_id: form.client_id || null,
      project_id: form.project_id || null,
      billable: form.billable,
      rebill_amount: Number(form.rebill_amount) || Number(form.amount) || 0,
    };
    return run(
      () => (isNew ? api.post("expenses", body) : api.put(`expenses/${expense.id}`, body)),
      isNew ? "Expense added" : "Expense saved"
    );
  };

  const remove = () => run(() => api.del(`expenses/${expense.id}`), "Expense deleted");

  async function upload(file) {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const res = await api.upload(`expenses/${expense.id}/receipt`, file);
      setReceiptKey(res.receipt_blob_key);
      toast.push("Receipt attached");
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      title={isNew ? "New expense" : "Edit expense"}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={remove}>
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? "Add expense" : "Save expense"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}

        <Fields cols={3}>
          <Field label="Date">
            <Input type="date" value={form.spent_on} onChange={(e) => set("spent_on", e.target.value)} />
          </Field>
          <Field label="Vendor">
            <Input value={form.vendor} placeholder="Figma" onChange={(e) => set("vendor", e.target.value)} />
          </Field>
          <Field label="Category">
            <Select value={form.category} onChange={(e) => set("category", e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Amount">
            <Input value={form.amount} placeholder="0.00" onChange={(e) => set("amount", e.target.value)} />
          </Field>
          <Field label={`${settings.tax_label} paid`} hint="Counts toward recoverable input tax">
            <Input value={form.tax_amount} placeholder="0.00" onChange={(e) => set("tax_amount", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Client">
            <Select value={form.client_id} onChange={(e) => set("client_id", e.target.value)}>
              <option value="">None</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Project">
            <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}>
              <option value="">None</option>
              {projects
                .filter((p) => !form.client_id || p.client_id === form.client_id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </Select>
          </Field>
        </Fields>

        <Check
          label="Rebill to client on their next invoice"
          checked={form.billable}
          onChange={(v) => set("billable", v)}
        />
        {form.billable && (
          <Field label="Rebill amount" hint={`Leave blank to rebill the full ${money(form.amount, sym)}`}>
            <Input value={form.rebill_amount} onChange={(e) => set("rebill_amount", e.target.value)} />
          </Field>
        )}

        <Field label="Receipt">
          {isNew ? (
            <div className="tiny muted">Save the expense first, then reopen it to attach a receipt.</div>
          ) : (
            <div className="row">
              <Input type="file" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
              {uploading ? (
                <span className="tiny muted nowrap">Uploading…</span>
              ) : receiptKey ? (
                <span
                  className="linky sm nowrap"
                  onClick={() =>
                    api.openBlob(api.filePath(receiptKey)).catch((err) => toast.push(err.message, "err"))
                  }
                >
                  View
                </span>
              ) : (
                <span className="tiny muted nowrap">None attached</span>
              )}
            </div>
          )}
        </Field>
      </Fields>
    </Modal>
  );
}
